// Terms of sounds, letters, syllables and stress (Kielioppi § 1, 01-sounds.mdx; lesson 1).
// HV facts as 01-sounds.mdx states them (the wiki's pronunciation appendix and Tips & Notes "Stress").
import { defineTerms } from './define';

export const terms = defineTerms({
  vokaali: {
    fi: {
      name: 'vokaali',
      short: '**Vokaali** eli ääntiö on äänne, jossa ilma virtaa suusta esteettä, kuten *a*, *e* ja *i*.',
      compare: 'Valyriassa vokaaleja on kuusi, *a e i o u y*; suomen *ä* ja *ö* puuttuvat.',
      long: 'Jokainen vokaali voi olla lyhyt tai pitkä. Vokaali on tavun ydin: jokaisessa tavussa on yksi vokaali tai diftongi.',
    },
    en: {
      name: 'vowel',
      short: 'A **vowel** is a sound made with the air flowing freely out of the mouth, such as *a*, *e* and *i*.',
      compare: 'High Valyrian has six vowels, *a e i o u y*; Finnish *ä* and *ö* are missing.',
      long: 'Each vowel can be short or long. A vowel is the core of a syllable: every syllable has one vowel or diphthong.',
    },
    related: ['pitka-vokaali', 'diftongi', 'konsonantti', 'tavu'],
    firstIn: 'letters',
  },
  konsonantti: {
    fi: {
      name: 'konsonantti',
      short: '**Konsonantti** eli kerake on äänne, jossa ilman kulku estyy tai ahtautuu jossakin suussa, kuten *k*, *s* tai *r*.',
      compare: 'Useimmat valyrian konsonantit ovat suomesta tuttuja; uusia ovat esimerkiksi [[q]], [[gh]], [[rh]] ja [[ñ]].',
    },
    en: {
      name: 'consonant',
      short: 'A **consonant** is a sound in which the air is stopped or squeezed somewhere in the mouth, such as *k*, *s* or *r*.',
      compare: 'Most High Valyrian consonants are familiar from Finnish; new ones include [[q]], [[gh]], [[rh]] and [[ñ]].',
    },
    related: ['vokaali', 'soinniton', 'klusiili'],
    firstIn: 'letters',
  },
  'pitka-vokaali': {
    fi: {
      name: 'pitkä vokaali',
      short: '**Pitkä vokaali** on sama vokaali pidempään sanottuna; valyriassa sen merkkinä on pituusviiva: [[ā ē ī ō ū ȳ]].',
      compare: 'Suomessa pituus kirjoitetaan kahdella kirjaimella, ja sekin erottaa sanoja: *tuli* ja *tuuli*.',
      long: 'Valyriassa pituus voi erottaa sekä sanoja että saman sanan muotoja. Pitkä vokaali tekee tavusta raskaan, joten se vaikuttaa myös painoon.',
    },
    en: {
      name: 'long vowel',
      short: 'A **long vowel** is the same vowel held longer; High Valyrian marks it with a macron: [[ā ē ī ō ū ȳ]].',
      compare: 'Finnish writes length with a double letter, and it tells words apart too: *tuli* ‘fire’ and *tuuli* ‘wind’.',
      long: 'In High Valyrian length can tell both words and forms of one word apart. A long vowel makes its syllable heavy, so it also affects stress.',
    },
    related: ['pituusviiva', 'raskas-tavu', 'paino'],
    firstIn: 'letters',
  },
  pituusviiva: {
    fi: {
      name: 'pituusviiva',
      short: '**Pituusviiva** (makroni) on vokaalin päällä oleva vaakaviiva, joka merkitsee vokaalin pitkäksi: [[ā]].',
      compare: 'Valyrian [[ā]] on kuin suomen *aa*: sama äänne, vain merkitty eri tavalla.',
    },
    en: {
      name: 'macron',
      short: 'A **macron** is the bar over a vowel that marks it long: [[ā]].',
      compare: 'High Valyrian [[ā]] is like Finnish *aa*: the same sound, written differently.',
    },
    related: ['pitka-vokaali'],
    firstIn: 'letters',
  },
  diftongi: {
    fi: {
      name: 'diftongi',
      short: '**Diftongi** on kahden vokaalin liukuma samassa tavussa, kuten suomen *ai* sanassa *aika*.',
      long: 'Valyriassa diftongit ovat joko laskevia (*ae*, *ao*) tai nousevia (*io*, *uē*). Laskeva diftongi tekee tavusta aina raskaan, nousevan raskaus riippuu sen jälkimmäisestä vokaalista. Siksi diftongit vaikuttavat painoon.',
    },
    en: {
      name: 'diphthong',
      short: 'A **diphthong** is a glide between two vowels in one syllable, like Finnish *ai* in *aika*.',
      long: 'High Valyrian diphthongs are either falling (*ae*, *ao*) or rising (*io*, *uē*). A falling diphthong always makes its syllable heavy; whether a rising one does depends on its second vowel. That is why diphthongs matter for stress.',
    },
    related: ['laskeva-diftongi', 'nouseva-diftongi', 'tavu', 'raskas-tavu'],
    firstIn: 'letters',
  },
  'laskeva-diftongi': {
    fi: {
      name: 'laskeva diftongi',
      short: '**Laskeva diftongi** alkaa täydellä vokaalilla ja liukuu heikompaan, kuten suomen *ai* ja *au*.',
      compare:
        'Valyriassa laskevat diftongit ovat [[ae]], [[āe]], [[ao]] ja [[āo]]; suomessa *ae* ja *ao* eivät ole diftongeja, joten älä jaa niitä kahteen tavuun.',
      long: 'Laskeva diftongi tekee tavusta aina raskaan.',
    },
    en: {
      name: 'falling diphthong',
      short: 'A **falling diphthong** starts on a full vowel and glides into a weaker one, like Finnish *ai* and *au*.',
      compare:
        'The High Valyrian falling diphthongs are [[ae]], [[āe]], [[ao]] and [[āo]]; in Finnish *ae* and *ao* are not diphthongs, so do not split them into two syllables.',
      long: 'A falling diphthong always makes its syllable heavy.',
    },
    related: ['diftongi', 'nouseva-diftongi', 'raskas-tavu'],
    firstIn: 'letters',
  },
  'nouseva-diftongi': {
    fi: {
      name: 'nouseva diftongi',
      short: '**Nouseva diftongi** alkaa lyhyellä liukuäänteellä *i* tai *u* ja liukuu siitä varsinaiseen vokaaliin.',
      compare: 'Suomessa ei ole nousevia diftongeja; lähimpänä on *j* vokaalin edellä, kuten sanassa *ja*.',
      long: 'Valyriassa nousevia diftongeja ovat [[ia]], [[ie]], [[io]], [[ua]] ja [[ue]], ja pitkällä jälkimmäisellä vokaalilla [[iā]], [[iē]], [[iō]], [[uā]] ja [[uē]]. Diftongi on kevyt, kun jälkimmäinen vokaali on lyhyt, ja raskas, kun se on pitkä.',
    },
    en: {
      name: 'rising diphthong',
      short: 'A **rising diphthong** starts with a short glide *i* or *u* and glides into the main vowel.',
      compare: 'Finnish has no rising diphthongs; the nearest thing is *j* before a vowel, as in *ja*.',
      long: 'The High Valyrian rising diphthongs are [[ia]], [[ie]], [[io]], [[ua]] and [[ue]], and with a long second vowel [[iā]], [[iē]], [[iō]], [[uā]] and [[uē]]. The diphthong is light when the second vowel is short and heavy when it is long.',
    },
    related: ['diftongi', 'laskeva-diftongi', 'liukuaanne', 'kevyt-tavu'],
    firstIn: 'letters',
  },
  liukuaanne: {
    fi: {
      name: 'liukuäänne',
      short: '**Liukuäänne** on lyhyt, vokaalin kaltainen äänne, joka ei muodosta omaa tavua, kuten nousevan diftongin alun *i* tai *u*.',
      compare: 'Suomen *j* on liukuäänne: *ja* on yksi tavu.',
    },
    en: {
      name: 'glide',
      short: 'A **glide** is a short, vowel-like sound that does not make a syllable of its own, such as the *i* or *u* at the start of a rising diphthong.',
      compare: 'Finnish *j* is a glide: *ja* is one syllable.',
    },
    related: ['nouseva-diftongi'],
    firstIn: 'letters',
  },
  tavu: {
    fi: {
      name: 'tavu',
      short: '**Tavu** on sanan ääntämisen yksikkö, jonka ytimenä on vokaali tai diftongi: *ta·lo*, *ai·ka*.',
      compare: 'Tavutus on suomesta tuttu, mutta valyriassa *ks*, *ts* ja *ps* aloittavat aina uuden tavun.',
      long: 'Valyrian tavu on kevyt tai raskas, ja se ratkaisee painon paikan. Sanan sisällä konsonantit siirtyvät seuraavan tavun alkuun niin moni kuin voi.',
    },
    en: {
      name: 'syllable',
      short: 'A **syllable** is a unit of a word’s pronunciation, built around a vowel or diphthong: *ta·lo*, *ai·ka*.',
      compare: 'Splitting words into syllables is familiar from Finnish, but in High Valyrian *ks*, *ts* and *ps* always start a new syllable.',
      long: 'A High Valyrian syllable is light or heavy, and that decides where the stress falls. Inside a word, consonants go to the start of the next syllable as many as can.',
    },
    related: ['kevyt-tavu', 'raskas-tavu', 'avoin-tavu', 'paino'],
    firstIn: 'stress',
  },
  'raskas-tavu': {
    fi: {
      name: 'raskas tavu',
      short: '**Raskas tavu** on tavu, jossa on pitkä vokaali tai laskeva diftongi tai joka päättyy konsonanttiin.',
      compare: 'Suomen pääpaino on aina ensimmäisellä tavulla, joten tavun raskaudella ei ole siinä väliä; valyriassa se ratkaisee, mihin paino osuu.',
    },
    en: {
      name: 'heavy syllable',
      short: 'A **heavy syllable** has a long vowel or a falling diphthong, or ends in a consonant.',
      compare: 'Finnish main stress is always on the first syllable, so weight does not matter for it; in High Valyrian it decides where the stress falls.',
    },
    related: ['kevyt-tavu', 'paino', 'pitka-vokaali', 'laskeva-diftongi'],
    firstIn: 'stress',
  },
  'kevyt-tavu': {
    fi: {
      name: 'kevyt tavu',
      short: '**Kevyt tavu** päättyy lyhyeen vokaaliin, kuten sanan *ta·lo* kumpikin tavu.',
      compare: 'Myös nouseva diftongi, jonka jälkimmäinen vokaali on lyhyt, on kevyt.',
    },
    en: {
      name: 'light syllable',
      short: 'A **light syllable** ends in a short vowel, like both syllables of Finnish *ta·lo*.',
      compare: 'A rising diphthong whose second vowel is short is light too.',
    },
    related: ['raskas-tavu', 'paino'],
    firstIn: 'stress',
  },
  'avoin-tavu': {
    fi: {
      name: 'avoin tavu',
      short: '**Avoin tavu** päättyy vokaaliin, kuten *ta-* sanassa *talo*; konsonanttiin päättyvä tavu on umpitavu.',
      compare: 'Valyriassa *ks*, *ts* ja *ps* siirtyvät seuraavaan tavuun, joten niitä edeltävä tavu jää avoimeksi: [[ry·tsas]].',
    },
    en: {
      name: 'open syllable',
      short: 'An **open syllable** ends in a vowel, like *ta-* in Finnish *talo*; a syllable ending in a consonant is closed.',
      compare: 'In High Valyrian *ks*, *ts* and *ps* go to the next syllable, so the syllable before them stays open: [[ry·tsas]].',
    },
    related: ['tavu', 'kevyt-tavu'],
    firstIn: 'stress',
  },
  paino: {
    fi: {
      name: 'paino',
      short: '**Paino** on se tavu, joka sanassa lausutaan voimakkaimmin.',
      compare: 'Suomessa paino on aina ensimmäisellä tavulla; valyriassa se lasketaan sanan lopusta.',
      long: 'Paino on tavallisesti toiseksi viimeisellä tavulla. Jos se on kevyt ja sitä edeltävä raskas, paino siirtyy kolmanneksi viimeiselle. Siksi paino liikkuu, kun sana saa päätteen. Käskymuodoissa paino on viimeisellä tavulla.',
    },
    en: {
      name: 'stress',
      short: '**Stress** is the syllable a word says most strongly.',
      compare: 'Finnish always stresses the first syllable; High Valyrian counts from the end of the word.',
      long: 'Stress is normally on the second-to-last syllable. If that one is light and the one before it heavy, stress moves back to the third-to-last. So stress moves when a word takes an ending. Imperatives are stressed on the last syllable.',
    },
    related: ['tavu', 'raskas-tavu', 'kevyt-tavu'],
    firstIn: 'stress',
  },
  ipa: {
    fi: {
      name: 'IPA',
      short:
        '**IPA** eli kansainvälinen foneettinen aakkosto on merkistö, jolla minkä tahansa kielen ääntäminen kirjoitetaan tarkasti, tavallisesti hakasulkeisiin: [ŋ].',
      compare: 'Suomen *ng* sanassa *kenkä* on IPA:lla [ŋ].',
      long: 'Sanakirja näyttää jokaisen sanan ääntämisen IPA-merkein, ja painollisen tavun edellä on merkki ˈ.',
    },
    en: {
      name: 'IPA',
      short:
        'The **IPA**, the International Phonetic Alphabet, is a set of symbols for writing down the pronunciation of any language exactly, usually in square brackets: [ŋ].',
      compare: 'The *ng* of Finnish *kenkä* is [ŋ] in the IPA.',
      long: 'The dictionary shows every word’s pronunciation in IPA, with the mark ˈ before the stressed syllable.',
    },
    related: ['paino'],
    firstIn: 'letters',
  },
  soinnillinen: {
    fi: {
      name: 'soinnillinen',
      short: '**Soinnillinen** äänne lausutaan äänihuulten väristessä, kuten *b*, *d*, *g* ja *z*.',
      compare: 'Kokeile kädellä kurkulla: *zzz* värisee, *sss* ei; valyrian [[z]] on soinnillinen *s*, jota suomessa ei ole.',
    },
    en: {
      name: 'voiced',
      short: 'A **voiced** sound is said with the vocal cords vibrating, such as *b*, *d*, *g* and *z*.',
      compare: 'Feel your throat: *zzz* buzzes, *sss* does not; High Valyrian [[z]] is a voiced *s*, which Finnish lacks.',
    },
    related: ['soinniton', 'konsonantti'],
    firstIn: 'letters',
  },
  soinniton: {
    fi: {
      name: 'soinniton',
      short: '**Soinniton** äänne lausutaan ilman äänihuulten värinää, kuten *p*, *t*, *k* ja *s*.',
      compare: 'Suomen *s* on aina soinniton, ja niin on valyriankin; valyriassa on lisäksi soinniton *r*, [[rh]].',
    },
    en: {
      name: 'voiceless',
      short: 'A **voiceless** sound is said without the vocal cords vibrating, such as *p*, *t*, *k* and *s*.',
      compare: 'Finnish *s* is always voiceless, and so is High Valyrian’s; High Valyrian also has a voiceless *r*, [[rh]].',
    },
    related: ['soinnillinen', 'konsonantti'],
    firstIn: 'letters',
  },
  taryaanne: {
    fi: {
      name: 'täryäänne',
      short: '**Täryäänne** syntyy, kun kielen kärki värisee nopeasti hammasvallia vasten, kuten suomen *r*.',
      compare: 'Valyrian *r* on täryäänne kuten suomessa; sarjoissa näyttelijät sanovat sen usein vain yhdellä napautuksella.',
    },
    en: {
      name: 'trill',
      short: 'A **trill** is made by the tip of the tongue vibrating fast against the ridge behind the teeth, like Finnish *r*.',
      compare: 'High Valyrian *r* is trilled as in Finnish; in the shows actors often say it with a single tap.',
    },
    related: ['soinniton'],
    firstIn: 'letters',
  },
  kitakieleke: {
    fi: {
      name: 'kitakieleke',
      short: '**Kitakieleke** on suun takaosassa, pehmeän suulaen reunassa riippuva pieni uloke; valyrian [[q]] äännetään kielen takaosalla sitä vasten.',
      compare: 'Suomessa sitä ei käytetä ääntämiseen: [[q]] on kuin *k* paljon taempaa.',
    },
    en: {
      name: 'uvula',
      short: 'The **uvula** is the small flap hanging at the back of the soft palate; High Valyrian [[q]] is made with the back of the tongue against it.',
      compare: 'Finnish does not use it: [[q]] is like a *k* made much further back.',
    },
    related: ['klusiili'],
    firstIn: 'letters',
  },
  palataali: {
    fi: {
      name: 'palataali',
      short: '**Palataali** eli liudentunut konsonantti lausutaan kieli kovaa suulakea vasten, jolloin siinä kuuluu *j*:n sävy, kuten [[ñ]] ja [[lj]].',
      compare: 'Suomen kirjakielessä liudennusta ei ole, mutta joissakin murteissa sitä kuulee.',
      long: 'Valyrian palataalit menettävät liudennuksensa päätteen *i*:n edellä, kuten lauseen [[Valar morghūlis]] verbissä.',
    },
    en: {
      name: 'palatal',
      short: 'A **palatal** consonant is made with the tongue against the hard palate, which gives it a *y*-like colour, such as [[ñ]] and [[lj]].',
      compare: 'Standard Finnish has no palatalisation, though some dialects do.',
      long: 'High Valyrian palatals lose their palatal colour before the *i* of an ending, as in the verb of [[Valar morghūlis]].',
    },
    related: ['konsonantti', 'nasaali'],
    firstIn: 'letters',
  },
  nasaali: {
    fi: {
      name: 'nasaali',
      short: '**Nasaali** eli nenä-äänne lausutaan ilman virratessa nenän kautta: *m*, *n* ja [ŋ].',
      compare: 'Suomessa samat: *m*, *n* ja *ng* sanassa *kenkä*.',
    },
    en: {
      name: 'nasal',
      short: 'A **nasal** is said with the air flowing out through the nose: *m*, *n* and [ŋ].',
      compare: 'Finnish has the same: *m*, *n* and the *ng* of *kenkä*.',
    },
    related: ['konsonantti', 'likvida'],
    firstIn: 'letters',
  },
  likvida: {
    fi: {
      name: 'likvida',
      short: '**Likvida** on yhteisnimi *l*- ja *r*-äänteille.',
      compare: 'Suomessa likvidat ovat samat, *l* ja *r*.',
    },
    en: {
      name: 'liquid',
      short: '**Liquid** is the common name for *l* and *r* sounds.',
      compare: 'Finnish has the same liquids, *l* and *r*.',
    },
    related: ['klusiili', 'tavu'],
    firstIn: 'stress',
  },
  sibilantti: {
    fi: {
      name: 'sibilantti',
      short: '**Sibilantti** eli suhuäänne on sihisevä äänne, kuten *s* ja *z*.',
      compare: 'Suomen omissa sanoissa ainoa sibilantti on *s*.',
    },
    en: {
      name: 'sibilant',
      short: 'A **sibilant** is a hissing sound, such as *s* and *z*.',
      compare: 'In native Finnish words the only sibilant is *s*.',
    },
    related: ['soinniton', 'tavu'],
    firstIn: 'stress',
  },
  klusiili: {
    fi: {
      name: 'klusiili',
      short: '**Klusiili** eli umpiäänne syntyy, kun ilmavirta pysäytetään hetkeksi ja päästetään irti: *p*, *t*, *k*, *b*, *d*, *g* ja [[q]].',
      compare: 'Suomen *p*, *t* ja *k* ovat klusiileja, ja valyriassakin ne äännetään ilman henkäystä.',
    },
    en: {
      name: 'stop',
      short: 'A **stop** is made by blocking the air for a moment and letting it go: *p*, *t*, *k*, *b*, *d*, *g* and [[q]].',
      compare: 'Finnish *p*, *t* and *k* are stops, and High Valyrian ones are unaspirated too.',
    },
    related: ['likvida', 'sibilantti', 'tavu'],
    firstIn: 'stress',
  },
  frikatiivi: {
    fi: {
      name: 'frikatiivi',
      short: '**Frikatiivi** eli hankausäänne syntyy, kun ilma puristuu kapeasta raosta, kuten *s*, *h* ja valyrian [[gh]].',
      compare: 'Suomen omissa sanoissa frikatiiveja ovat *s* ja *h*.',
    },
    en: {
      name: 'fricative',
      short: 'A **fricative** is made by squeezing the air through a narrow gap, such as *s*, *h* and High Valyrian [[gh]].',
      compare: 'In native Finnish words the fricatives are *s* and *h*.',
    },
    related: ['klusiili', 'sibilantti'],
    firstIn: 'verb-classes',
  },
  huulikonsonantti: {
    fi: {
      name: 'huulikonsonantti',
      short: '**Huulikonsonantti** eli labiaali lausutaan huulilla: *p*, *b*, *m*, *v* ja *f*.',
      compare: 'Suomessa samoin: *p*, *m* ja *v* ovat huuliäänteitä.',
    },
    en: {
      name: 'labial',
      short: 'A **labial** consonant is made with the lips: *p*, *b*, *m*, *v* and *f*.',
      compare: 'The same in Finnish: *p*, *m* and *v* are labials.',
    },
    related: ['konsonantti'],
    firstIn: 'adj-position',
  },
  // Kielioppi upgrade step 2, group 1 (01-sounds.mdx): written double consonants are long (geminates):
  // Peterson, dedalvs.tumblr.com/post/108188435345 ("all consonants can be geminate except the palatals");
  // the wiki's IPA writes them double ([ˈissa]) and splits them between syllables.
  kaksoiskonsonantti: {
    fi: {
      name: 'kaksoiskonsonantti',
      short: '**Kaksoiskonsonantti** on kahdella samalla kirjaimella kirjoitettu pitkä konsonantti, kuten *ss* sanassa *kissa*.',
      compare: 'Valyriassa se äännetään pitkänä kuten suomessa, ja tavuraja jakaa sen kahtia: [[is·sa]].',
      long: 'Kaksoiskonsonantin alkupuoli päättää edellisen tavun, joten se tavu on raskas ja voi saada painon: [[jen·TYS·sy]].',
    },
    en: {
      name: 'double consonant',
      short: 'A **double consonant** is a long consonant written with two of the same letter, like *ss* in Finnish *kissa*.',
      compare: 'It is said long, as in Finnish, and the syllable break splits it: [[is·sa]].',
      long: 'Its first half closes the syllable before it, so that syllable is heavy and can take the stress: [[jen·TYS·sy]].',
    },
    related: ['konsonantti', 'raskas-tavu', 'tavu'],
    firstIn: 'letters',
  },
});
